import React, { useEffect, useState } from 'react';
import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
import { QRCode } from 'react-qrcode-logo';
import DOMPurify from 'dompurify';
import QRScannerModern from './QRScannerModern';
import Button from './styled/Button';
import Card, { CardHeader, CardBody } from './styled/Card';
import Input from './styled/Input';
import BaseFormTemplate from '../form-templates/BaseFormTemplate';
import formTemplateManager from '../form-templates/FormTemplateManager';
import { useTranslation } from 'react-i18next';

export default function SafetyPortal() {
  const { t, i18n } = useTranslation();
  const [formType, setFormType] = useState('field-assessment');
  const [formData, setFormData] = useState({});
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [statusMessage, setStatusMessage] = useState('');
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [documentNumberQuery, setDocumentNumberQuery] = useState('');
  const [trackedForm, setTrackedForm] = useState(null);
  const [allForms, setAllForms] = useState([]);
  const [lastSubmittedDocNumber, setLastSubmittedDocNumber] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [viewMode, setViewMode] = useState('create'); // 'create', 'list', 'view', 'edit'
  const [selectedForm, setSelectedForm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [availableFormTypes, setAvailableFormTypes] = useState([]);

  useEffect(() => {
    // Initialize available form types
    try {
      const types = formTemplateManager.getAvailableTypes();
      setAvailableFormTypes(types);
    } catch (error) {
      console.error('Error initializing form types:', error);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const renderFormUsingTemplate = () => {
    if (!formType) return null;

    try {
      const template = formTemplateManager.getTemplate(formType, t);
      
      // Localize template titles and descriptions
      const localizedTitle = formTemplateManager.getLocalizedTitle(template, language);
      const localizedDescription = formTemplateManager.getLocalizedDescription(template, language);
      
      // Localize field labels, placeholders, and options
      const localizedFields = template.fields.map(field => ({
        ...field,
        label: formTemplateManager.getLocalizedFieldLabel(field, language),
        placeholder: formTemplateManager.getLocalizedFieldPlaceholder(field, language),
        options: formTemplateManager.getLocalizedFieldOptions(field.options, language)
      }));

      return (
        <BaseFormTemplate
          title={localizedTitle}
          description={localizedDescription}
          fields={localizedFields}
          formData={formData}
          onSubmit={handleTemplateSubmit}
          onCancel={() => setViewMode('list')}
          onFieldChange={(name, value) => 
            setFormData(prev => ({ ...prev, [name]: value }))
          }
        />
      );
    } catch (error) {
      console.error('Error rendering form template:', error);
      return (
        <Card className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">
            {t('form.templateError', 'Error Loading Form Template')}
          </h2>
          <p className="text-gray-700 mb-4">
            {t('form.templateErrorMessage', 'Unable to load the form template. Please try again later.')}
          </p>
          <p className="text-sm text-gray-500">
            {error.message}
          </p>
          <Button
            variant="primary"
            onClick={() => setViewMode('list')}
          >
            {t('form.backToList', 'Back to Form List')}
          </Button>
        </Card>
      );
    }
  };

  const handleTemplateSubmit = async (formData) => {
    try {
      setIsLoading(true);
      setStatusMessage(t('form.submitting', 'Submitting form...'));

      const payload = {
        type: formType,
        data: formData,
        language: language
      };

      const response = await axios.post(`${API_BASE_URL}/api/forms`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setStatusMessage(t('form.submittedSuccess', 'Form submitted successfully!'));
      setLastSubmittedDocNumber(response.data.documentNumber);
      setFormData({});

      // Reload forms list
      await loadAllForms();

      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                           error.message || 
                           t('form.submitFailed', 'Failed to submit form');
      setStatusMessage(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedForm(null);
    setFormData({});
    loadAllForms();
  };

  const loadAllForms = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllForms(res.data);
      setSearchResults(res.data);
    } catch (err) {
      console.error('Failed to load forms:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadAllForms();
      return;
    }
    
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/forms/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data.forms);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteForm = async (formId) => {
    if (window.confirm(language === 'en' ? 'Are you sure you want to delete this form?' : 'คุณแน่ใจหรือไม่ว่าต้องการลบแบบฟอร์มนี้?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/forms/${formId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStatusMessage(language === 'en' ? 'Form deleted successfully' : 'ลบแบบฟอร์มสำเร็จ');
        loadAllForms();
        if (selectedForm && selectedForm._id === formId) {
          setViewMode('list');
          setSelectedForm(null);
        }
      } catch (err) {
        setStatusMessage(language === 'en' ? 'Failed to delete form' : 'ไม่สามารถลบแบบฟอร์มได้');
      }
    }
  };

  const handleUpdateForm = async () => {
    if (!selectedForm) return;
    
    const payload = { 
      type: formType, 
      data: formData, 
      language: selectedForm.language || 'en'
    };
    
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/forms/${selectedForm._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatusMessage(language === 'en' ? 'Form updated successfully' : 'อัปเดตแบบฟอร์มสำเร็จ');
      setSelectedForm(res.data);
      setViewMode('view');
      loadAllForms();
    } catch (err) {
      setStatusMessage(err.response?.data?.error || 'Update failed');
    }
  };

  // Offline upload logic
  useEffect(() => {
    const uploadQueued = async () => {
      if (navigator.onLine && offlineQueue.length > 0) {
        for (const item of offlineQueue) {
          try {
            await axios.post(
              `${API_BASE_URL}/api/forms`,
              item,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setOfflineQueue(q => q.filter(qi => qi !== item));
          } catch (e) {}
        }
      }
    };
    window.addEventListener('online', uploadQueued);
    return () => window.removeEventListener('online', uploadQueued);
  }, [offlineQueue, token]);

  // Removed legacy handleSubmit function as we're now using the template-based form system

  const handlePrintQR = () => {
    const printWindow = window.open('', '_blank');
    
    // Sanitize the document number to prevent XSS
    const sanitizedDocNumber = DOMPurify.sanitize(lastSubmittedDocNumber || 'Unknown');
    
    const qrCanvas = document.getElementById('qr-code');
    if (!qrCanvas) {
      alert('QR Code not found. Please generate a QR code first.');
      return;
    }
    
    const qrDataURL = qrCanvas.toDataURL();
    
    // Build HTML content safely
    const htmlContent = `
      <html>
        <head>
          <title>QR Code</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h3 { color: #333; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          <h3>QR Code for Document: ${sanitizedDocNumber}</h3>
          <img src="${qrDataURL}" alt="QR Code for document ${sanitizedDocNumber}" />
          <p>Generated on: ${new Date().toLocaleString()}</p>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const toggleLanguage = () => {
    setLanguage(prev => (prev === 'en' ? 'th' : 'en'));
  };

  const handleScan = (data) => {
    if (data) {
      setDocumentNumberQuery(data.replace(/^.*\//, ''));
      setShowScanner(false);
    }
  };

  const renderFields = () => {
    const lang = language;
    switch (formType) {
      case 'audit':
        return (
          <>
            <Input
              label={lang === 'en' ? 'Documentation' : 'เอกสาร'}
              name="documentation"
              value={formData.documentation || ''}
              onChange={handleChange}
              type="textarea"
              rows={4}
              lang={lang}
              fullWidth
            />
            <Input
              label={lang === 'en' ? 'PPE Usage' : 'การใช้ PPE'}
              name="ppeUsage"
              value={formData.ppeUsage || ''}
              onChange={handleChange}
              type="textarea"
              rows={4}
              lang={lang}
              fullWidth
            />
            <Input
              label={lang === 'en' ? 'Machine Safety' : 'ความปลอดภัยของเครื่องจักร'}
              name="machineSafety"
              value={formData.machineSafety || ''}
              onChange={handleChange}
              type="textarea"
              rows={4}
              lang={lang}
              fullWidth
            />
          </>
        );
      case 'jsa':
        return (
          <>
            <Input
              label={lang === 'en' ? 'Job/Task' : 'งาน/ภารกิจ'}
              name="jobTask"
              value={formData.jobTask || ''}
              onChange={handleChange}
              lang={lang}
              fullWidth
            />
            <Input
              label={lang === 'en' ? 'Hazards' : 'อันตราย'}
              name="hazards"
              value={formData.hazards || ''}
              onChange={handleChange}
              type="textarea"
              rows={4}
              lang={lang}
              fullWidth
            />
            <Input
              label={lang === 'en' ? 'Controls' : 'มาตรการควบคุม'}
              name="controls"
              value={formData.controls || ''}
              onChange={handleChange}
              type="textarea"
              rows={4}
              lang={lang}
              fullWidth
            />
          </>
        );
      case 'incident':
        return (
          <>
            <Input
              label={lang === 'en' ? 'Date of Incident' : 'วันที่เกิดเหตุ'}
              name="incidentDate"
              type="date"
              value={formData.incidentDate || ''}
              onChange={handleChange}
              fullWidth
            />
            <Input
              label={lang === 'en' ? 'Description' : 'รายละเอียดเหตุการณ์'}
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              type="textarea"
              rows={4}
              lang={lang}
              fullWidth
            />
            <Input
              label={lang === 'en' ? 'Root Cause' : 'สาเหตุรากฐาน'}
              name="rootCause"
              value={formData.rootCause || ''}
              onChange={handleChange}
              type="textarea"
              rows={4}
              lang={lang}
              fullWidth
            />
          </>
        );
      default:
        return null;
    }
  };

  const renderFormList = () => {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {language === 'en' ? 'All Forms' : 'แบบฟอร์มทั้งหมด'}
          </h2>
          <Button
            onClick={() => setViewMode('create')}
            variant="primary"
            size="md"
          >
            {language === 'en' ? 'Create New Form' : 'สร้างแบบฟอร์มใหม่'}
          </Button>
        </div>
        
        <Card className="p-6">
          <div className="flex gap-3 mb-4">
            <div className="flex-1">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'en' ? 'Search forms...' : 'ค้นหาแบบฟอร์ม...'}
                fullWidth
              />
            </div>
            <Button
              onClick={handleSearch}
              variant="secondary"
              size="md"
            >
              {language === 'en' ? 'Search' : 'ค้นหา'}
            </Button>
          </div>

          {isLoading && (
            <div className="text-center py-8">
              <p className="text-gray-600">{language === 'en' ? 'Loading...' : 'กำลังโหลด...'}</p>
            </div>
          )}
          
          <div className="grid gap-4">
            {searchResults.map((form) => (
              <Card key={form._id} variant="default" className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {form.type === 'audit' && (language === 'en' ? 'Safety Audit' : 'การตรวจสอบความปลอดภัย')}
                      {form.type === 'jsa' && (language === 'en' ? 'Job Safety Analysis' : 'การวิเคราะห์ความปลอดภัยของงาน')}
                      {form.type === 'incident' && (language === 'en' ? 'Incident Report' : 'รายงานเหตุการณ์')}
                    </h3>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600">
                        {language === 'en' ? 'Document #' : 'เอกสาร #'}: {form.documentNumber}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(form.submittedAt).toLocaleDateString()}
                      </p>
                      <p className="text-sm">
                        {form.status === 'open' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {language === 'en' ? 'Open' : 'เปิด'}
                          </span>
                        )}
                        {form.status === 'in-progress' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {language === 'en' ? 'In Progress' : 'กำลังดำเนินการ'}
                          </span>
                        )}
                        {form.status === 'closed' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {language === 'en' ? 'Closed' : 'ปิด'}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      onClick={() => handleFormSelect(form)}
                      variant="primary"
                      size="sm"
                    >
                      {language === 'en' ? 'View' : 'ดู'}
                    </Button>
                    <Button
                      onClick={() => handleEditForm(form)}
                      variant="secondary"
                      size="sm"
                    >
                      {language === 'en' ? 'Edit' : 'แก้ไข'}
                    </Button>
                    <Button
                      onClick={() => handleDeleteForm(form._id)}
                      variant="danger"
                      size="sm"
                    >
                      {language === 'en' ? 'Delete' : 'ลบ'}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  const renderFormView = () => {
    if (!selectedForm) return null;
    
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">
            {selectedForm.type === 'audit' && (language === 'en' ? 'Safety Audit' : 'การตรวจสอบความปลอดภัย')}
            {selectedForm.type === 'jsa' && (language === 'en' ? 'Job Safety Analysis' : 'การวิเคราะห์ความปลอดภัยของงาน')}
            {selectedForm.type === 'incident' && (language === 'en' ? 'Incident Report' : 'รายงานเหตุการณ์')}
          </h2>
          <button
            onClick={handleBackToList}
            className="bg-gray-500 text-white px-4 py-2 rounded"
          >
            {language === 'en' ? 'Back to List' : 'กลับไปที่รายการ'}
          </button>
        </div>
        
        <div className="border rounded p-4">
          <p className="text-sm text-gray-600 mb-2">
            {language === 'en' ? 'Document #' : 'เอกสาร #'}: {selectedForm.documentNumber}
          </p>
          <p className="text-sm text-gray-600 mb-4">
            {new Date(selectedForm.submittedAt).toLocaleString()}
          </p>
          
          {selectedForm.type === 'audit' && (
            <div className="space-y-3">
              <div>
                <label className="font-semibold">{language === 'en' ? 'Documentation' : 'เอกสาร'}:</label>
                <p>{selectedForm.data?.documentation || '-'}</p>
              </div>
              <div>
                <label className="font-semibold">{language === 'en' ? 'PPE Usage' : 'การใช้ PPE'}:</label>
                <p>{selectedForm.data?.ppeUsage || '-'}</p>
              </div>
              <div>
                <label className="font-semibold">{language === 'en' ? 'Machine Safety' : 'ความปลอดภัยของเครื่องจักร'}:</label>
                <p>{selectedForm.data?.machineSafety || '-'}</p>
              </div>
            </div>
          )}
          
          {selectedForm.type === 'jsa' && (
            <div className="space-y-3">
              <div>
                <label className="font-semibold">{language === 'en' ? 'Job/Task' : 'งาน/ภารกิจ'}:</label>
                <p>{selectedForm.data?.jobTask || '-'}</p>
              </div>
              <div>
                <label className="font-semibold">{language === 'en' ? 'Hazards' : 'อันตราย'}:</label>
                <p>{selectedForm.data?.hazards || '-'}</p>
              </div>
              <div>
                <label className="font-semibold">{language === 'en' ? 'Controls' : 'มาตรการควบคุม'}:</label>
                <p>{selectedForm.data?.controls || '-'}</p>
              </div>
            </div>
          )}
          
          {selectedForm.type === 'incident' && (
            <div className="space-y-3">
              <div>
                <label className="font-semibold">{language === 'en' ? 'Date of Incident' : 'วันที่เกิดเหตุ'}:</label>
                <p>{selectedForm.data?.incidentDate || '-'}</p>
              </div>
              <div>
                <label className="font-semibold">{language === 'en' ? 'Description' : 'รายละเอียดเหตุการณ์'}:</label>
                <p>{selectedForm.data?.description || '-'}</p>
              </div>
              <div>
                <label className="font-semibold">{language === 'en' ? 'Root Cause' : 'สาเหตุรากฐาน'}:</label>
                <p>{selectedForm.data?.rootCause || '-'}</p>
              </div>
            </div>
          )}
          
          <div className="mt-4 pt-4 border-t">
            <div className="flex gap-2">
              <button
                onClick={() => handleEditForm(selectedForm)}
                className="bg-yellow-500 text-white px-4 py-2 rounded"
              >
                {language === 'en' ? 'Edit' : 'แก้ไข'}
              </button>
              <button
                onClick={() => handleDeleteForm(selectedForm._id)}
                className="bg-red-500 text-white px-4 py-2 rounded"
              >
                {language === 'en' ? 'Delete' : 'ลบ'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFormEdit = () => {
    if (!selectedForm) return null;
    
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">
            {language === 'en' ? 'Edit Form' : 'แก้ไขแบบฟอร์ม'}
          </h2>
          <button
            onClick={handleBackToList}
            className="bg-gray-500 text-white px-4 py-2 rounded"
          >
            {language === 'en' ? 'Cancel' : 'ยกเลิก'}
          </button>
        </div>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {language === 'th'
              ? 'กรุณาเปลี่ยนแป้นพิมพ์ของคุณเป็นภาษาไทยก่อนเริ่มพิมพ์'
              : 'Please switch your keyboard to Thai before typing if needed'}
          </p>
          {renderFields()}
          <div className="flex gap-2">
            <button
              onClick={handleUpdateForm}
              className="bg-green-600 text-white px-4 py-2 rounded"
            >
              {language === 'en' ? 'Update' : 'อัปเดต'}
            </button>
            <button
              onClick={handleBackToList}
              className="bg-gray-500 text-white px-4 py-2 rounded"
            >
              {language === 'en' ? 'Cancel' : 'ยกเลิก'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateForm = () => {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('form.createNew', 'Create New Form')}
          </h2>
          
          <div className="mb-8 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <label className="block text-lg font-bold text-gray-900 mb-3">
              {t('form.selectType', 'Select Form Type')}
            </label>
            <select
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              className="w-full px-4 py-3 text-base border-2 border-gray-400 rounded-lg shadow-lg focus:outline-none focus:ring-4 focus:ring-hemp-green-300 focus:border-hemp-green-600 font-semibold"
            >
              {availableFormTypes.map(type => (
                <option key={type} value={type}>
                  {type.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </option>
              ))}
            </select>
          </div>

          {renderFormUsingTemplate()}
        </Card>

        {statusMessage && (
          <Card className="p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-800">{statusMessage}</p>
          </Card>
        )}

        {lastSubmittedDocNumber && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">
              {t('form.qrCodeTitle', 'QR Code for Document')}
            </h3>
            <div className="flex flex-col items-center">
              <QRCode
                id="qr-code"
                value={`https://your-domain.com/track/${lastSubmittedDocNumber}`}
                size={160}
                fgColor="#000000"
                bgColor="#ffffff"
                quietZone={10}
                ecLevel="H"
              />
              <p className="mt-2 text-sm text-gray-600">
                {t('form.documentNumber', 'Document Number')}: {lastSubmittedDocNumber}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  
                  // Sanitize all user inputs to prevent XSS
                  const sanitizedDocNumber = DOMPurify.sanitize(lastSubmittedDocNumber || 'Unknown');
                  const sanitizedTitle = DOMPurify.sanitize(t('form.qrCodeTitle', 'QR Code for Document'));
                  const sanitizedDocLabel = DOMPurify.sanitize(t('form.documentNumber', 'Document Number'));
                  const sanitizedPrintLabel = DOMPurify.sanitize(t('form.print', 'Print QR Code'));
                  
                  // Get QR code element safely
                  const qrElement = document.getElementById('qr-code');
                  if (!qrElement) {
                    alert('QR Code not found. Please generate a QR code first.');
                    return;
                  }
                  
                  const qrHTML = qrElement.outerHTML;
                  
                  const htmlContent = `
                    <html>
                      <head>
                        <title>QR Code - ${sanitizedDocNumber}</title>
                        <style>
                          body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
                          .qr-container { margin: 20px 0; }
                          .doc-number { font-weight: bold; margin: 10px 0; }
                        </style>
                      </head>
                      <body>
                        <h2>${sanitizedTitle}</h2>
                        <div class="qr-container">
                          ${qrHTML}
                        </div>
                        <div class="doc-number">
                          ${sanitizedDocLabel}: ${sanitizedDocNumber}
                        </div>
                        <button onclick="window.print()" style="padding: 10px 20px; background: #22c55e; color: white; border: none; border-radius: 4px; cursor: pointer;">
                          ${sanitizedPrintLabel}
                        </button>
                        <p style="font-size: 12px; color: #666; margin-top: 20px;">
                          Generated on: ${new Date().toLocaleString()}
                        </p>
                      </body>
                    </html>
                  `;
                  
                  printWindow.document.write(htmlContent);
                  printWindow.document.close();
                }}
                className="mt-4"
              >
                {t('form.printQR', 'Print QR Code')}
              </Button>
            </div>
          </Card>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (viewMode === 'list') {
      loadAllForms();
    }
  }, [viewMode]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-4 text-gray-800">
        {language === 'en' ? 'Anantarra Digital Portal' : 'พอร์ทัลดิจิทัล อนันต์ธารา'}
      </h1>
      
      <div className="flex gap-2 mb-6">
        <Button
          variant={viewMode === 'create' ? 'primary' : 'outline'}
          size="md"
          onClick={() => setViewMode('create')}
        >
          {language === 'en' ? 'Create' : 'สร้าง'}
        </Button>
        <Button
          variant={viewMode === 'list' ? 'primary' : 'outline'}
          size="md"
          onClick={() => setViewMode('list')}
        >
          {language === 'en' ? 'List' : 'รายการ'}
        </Button>
        <Button
          variant="ghost"
          size="md"
          onClick={toggleLanguage}
        >
          {language === 'en' ? 'Switch to Thai (ภาษาไทย)' : 'เปลี่ยนเป็นภาษาอังกฤษ (EN)'}
        </Button>
      </div>

      {viewMode === 'create' && renderCreateForm()}
      {viewMode === 'list' && renderFormList()}
      {viewMode === 'view' && renderFormView()}
      {viewMode === 'edit' && renderFormEdit()}

      <div className="mt-8 pt-6 border-t border-gray-200">
        <Button
          variant="secondary"
          size="md"
          onClick={() => setShowScanner(true)}
        >
          {language === 'en' ? 'Contact Point Inquiry' : 'สอบถามจุดติดต่อ'}
        </Button>
        {showScanner && <QRScannerModern onScan={handleScan} onClose={() => setShowScanner(false)} />}
      </div>
    </div>
  );
}
